import * as Sentry from '@sentry/react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import {
  getCurrentUser,
  getCurrentSession,
  loginUser,
  logoutUser,
  registerUser,
  onAuthStateChange,
  type LoginParams,
  type RegisterParams,
  type LoginResult,
  type RegisterResult,
  type LogoutResult,
} from '../lib/supabase/auth'
import type { AuthUser, AuthSession } from '../lib/supabase/types'
import { supabase } from '../lib/supabase/client'
import { wipeUserLocalState } from '../lib/auth/wipeLocalState'
import { identifyUser, resetAnalytics } from '../lib/analytics'

/**
 * Authentication Context Provider
 * Manages auth state and provides auth functions to the app
 */

interface AuthContextValue {
  user: AuthUser | null
  session: AuthSession | null
  loading: boolean
  isAuthenticated: boolean

  // Auth actions
  login: (params: LoginParams) => Promise<LoginResult>
  register: (params: RegisterParams) => Promise<RegisterResult>
  logout: () => Promise<LogoutResult>
}

// Exported for components that render outside the provider tree (e.g. Titlebar)
// and need safe access via useContext() which returns undefined instead of throwing.
export const AuthContext = createContext<AuthContextValue | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { t } = useTranslation()
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, setSession] = useState<AuthSession | null>(null)
  const [loading, setLoading] = useState(true)

  // Track current user ID to skip redundant state updates.
  // Supabase fires onAuthStateChange multiple times on login (INITIAL_SESSION,
  // SIGNED_IN, TOKEN_REFRESHED) — each would trigger a full re-render cascade
  // through MainLayout → useTabManager, destabilizing active tab state.
  const userIdRef = useRef<string | null>(null)

  // Note: Recovery token handling is done in App.tsx BEFORE router initialization
  // This prevents BrowserRouter from treating the hash as a route

  // Initialize auth state — session-first for offline resilience
  useEffect(() => {
    const initAuth = async () => {
      try {
        // getSession() reads from localStorage cache — works offline
        const currentSession = await getCurrentSession()
        setSession(currentSession)

        if (currentSession?.user) {
          // Use cached session user immediately
          userIdRef.current = currentSession.user.id
          setUser(currentSession.user)

          // Try to validate via network (non-blocking)
          try {
            const currentUser = await getCurrentUser()
            if (currentUser) {
              userIdRef.current = currentUser.id
              setUser(currentUser)
            }
          } catch {
            // Network unavailable — cached session user is still valid
            console.warn('[Auth] Could not validate user online, using cached session')
          }
        } else {
          // No cached session — try network as last resort
          try {
            const currentUser = await getCurrentUser()
            if (currentUser) {
              userIdRef.current = currentUser.id
            }
            setUser(currentUser)
          } catch {
            // Fully offline with no cached session
          }
        }
      } catch (error) {
        console.error('[Auth] Initialization error:', error)
      } finally {
        setLoading(false)
      }
    }

    initAuth()
  }, [])

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (newUser) => {
      // Hard `is_active` gate — runs on EVERY event that carries a user
      // (SIGNED_IN, TOKEN_REFRESHED, INITIAL_SESSION) BEFORE the dedup
      // skip, so an admin flipping `is_active` to false kicks the
      // client out within one refresh cycle (~50 min) instead of
      // sitting idle until the token actually expires. The profiles
      // select is the same row we already need for the onboarding
      // flag — folded into one query.
      if (newUser && supabase) {
        try {
          const { data } = await supabase
            .from('profiles')
            .select('is_active, setup_flow_completed')
            .eq('id', newUser.id)
            .maybeSingle()
          if (data?.is_active === false) {
            // Deactivated → tear down the session; Dexie is intentionally
            // NOT wiped (per product decision — local data survives so
            // re-activation lets the user pick up where they left off).
            // The generic toast wording matches the invalid-credentials
            // path so the deactivation isn't observable.
            toast.error(t('setup.signin.errorGeneric'))
            await supabase.auth.signOut()
            return
          }
          // Existing onboarding-flag rehydration, kept in the same
          // query to avoid a second round-trip on every SIGNED_IN.
          if (
            data?.setup_flow_completed &&
            localStorage.getItem('onboarding_completed') !== 'true'
          ) {
            localStorage.setItem('onboarding_completed', 'true')
          }
        } catch (err) {
          console.warn('[Auth] Failed to load profile flags:', err)
        }
      }

      // Skip redundant updates — same user ID means no meaningful state change.
      // Prevents unnecessary re-render cascades that destabilize tab navigation.
      if (newUser?.id === userIdRef.current && newUser !== null) return

      // User-id-mismatch check — safety net for sessions that were not logged
      // out cleanly (silent session expire, crash mid-logout, user switching
      // accounts without explicit logout). When the newly-logged-in user
      // differs from the last-known user, wipe stale local state BEFORE
      // committing the new user to React state so the next render reads fresh flags.
      if (newUser) {
        const lastUserId = localStorage.getItem('lastUserId')
        if (lastUserId && lastUserId !== newUser.id) {
          console.warn('[Auth] User ID changed without clean logout — wiping stale state')
          await wipeUserLocalState()
        }
        localStorage.setItem('lastUserId', newUser.id)
      }

      userIdRef.current = newUser?.id ?? null
      setUser(newUser)

      // Refresh session when user changes
      if (newUser) {
        getCurrentSession().then(setSession)
        // Note: OAuth callback handling is now done in OAuthCallbackPage component
        identifyUser(newUser.id, {
          platform: window.api ? 'electron' : 'web',
        })
        Sentry.setUser({ id: newUser.id, email: newUser.email })
      } else {
        setSession(null)
        resetAnalytics()
        Sentry.setUser(null)
      }
    })

    return unsubscribe
  }, [])

  // Dedicated listener for PASSWORD_RECOVERY — Supabase's PKCE recovery flow
  // exchanges a `?code=` param for a session and fires a PASSWORD_RECOVERY event
  // on the client. No `access_token=` in the URL hash anymore, so the legacy
  // detector in index.html misses it. We catch it here and set the flag that
  // SetupRoute / SetupPage use to show ResetPasswordForm.
  useEffect(() => {
    if (!supabase) return
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        localStorage.setItem('passwordRecoveryMode', 'true')
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  // Auth actions
  const handleLogin = useCallback(async (params: LoginParams): Promise<LoginResult> => {
    const result = await loginUser(params)

    if (result.success && result.user && result.session) {
      userIdRef.current = result.user.id
      setUser(result.user)
      setSession(result.session)
    }

    return result
  }, [])

  const handleRegister = useCallback(async (params: RegisterParams): Promise<RegisterResult> => {
    const result = await registerUser(params)

    if (result.success && result.user) {
      userIdRef.current = result.user.id
      setUser(result.user)
    }

    return result
  }, [])

  const handleLogout = useCallback(async (): Promise<LogoutResult> => {
    const result = await logoutUser()

    if (result.success) {
      userIdRef.current = null
      setUser(null)
      setSession(null)
      // Wipe Dexie + user-scoped localStorage AFTER successful signOut.
      // If signOut fails (e.g. network), we intentionally leave state intact
      // so the user can retry without losing local data.
      await wipeUserLocalState()
    }

    return result
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    user,
    session,
    loading,
    isAuthenticated: !!user,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
  }), [user, session, loading, handleLogin, handleRegister, handleLogout])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook to use auth context
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
